import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarCheck, X } from 'lucide-react';
const NAMES = ['Carlos R.', 'María L.', 'José M.', 'Ana P.', 'Luis G.', 'Carmen S.', 'Miguel A.', 'Rosa D.', 'Pedro H.', 'Laura V.', 'Fernando T.', 'Patricia N.', 'Roberto C.', 'Gabriela F.', 'Andrés B.', 'Sofía E.', 'Diego O.', 'Valentina Q.', 'Alejandro J.', 'Isabella K.', 'Ricardo W.', 'Daniela Z.', 'Jorge I.', 'Camila U.', 'Eduardo Y.', 'Natalia X.', 'Sergio P.', 'Adriana M.', 'Raúl T.', 'Mónica R.'];
const CITIES = ['Miami, FL', 'Houston, TX', 'Los Angeles, CA', 'New York, NY', 'Chicago, IL', 'Dallas, TX', 'San Antonio, TX', 'Phoenix, AZ', 'San Diego, CA', 'Orlando, FL', 'Denver, CO', 'Las Vegas, NV', 'Atlanta, GA', 'Charlotte, NC', 'Tampa, FL', 'Austin, TX', 'San Jose, CA', 'Jacksonville, FL', 'Fort Worth, TX', 'El Paso, TX', 'Albuquerque, NM', 'Tucson, AZ', 'Sacramento, CA', 'Riverside, CA'];
const MESSAGES = ['acaba de reservar su consulta estratégica', 'acaba de agendar su evaluación de crédito', 'acaba de reservar su auditoría de crédito', 'acaba de agendar su consulta gratuita'];
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval(): number {
  return Math.floor(Math.random() * 7000) + 8000; // 8–15 seconds
}
interface Notification {
  id: number;
  name: string;
  city: string;
  message: string;
  timeAgo: string;
}
function getTimeAgo(): string {
  const minutes = Math.floor(Math.random() * 15) + 1;
  return `hace ${minutes} min`;
}
export function RecentBookingToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const recentNames = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idCounter = useRef(0);
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const pickName = useCallback((): string => {
    const available = NAMES.filter(n => !recentNames.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = getRandomItem(pool);
    recentNames.current.push(name);
    if (recentNames.current.length > 10) {
      recentNames.current.shift();
    }
    return name;
  }, []);
  const showNotification = useCallback(() => {
    const newNotification: Notification = {
      id: idCounter.current++,
      name: pickName(),
      city: getRandomItem(CITIES),
      message: getRandomItem(MESSAGES),
      timeAgo: getTimeAgo()
    };
    setNotification(newNotification);
    // Auto-dismiss after 4 seconds
    if (dismissRef.current) clearTimeout(dismissRef.current);
    dismissRef.current = setTimeout(() => {
      setNotification(null);
    }, 4000);
  }, [pickName]);
  const scheduleNext = useCallback(() => {
    timerRef.current = setTimeout(() => {
      showNotification();
      scheduleNext();
    }, getRandomInterval());
  }, [showNotification]);
  useEffect(() => {
    // First notification after 3 seconds
    const initialTimer = setTimeout(() => {
      showNotification();
      scheduleNext();
    }, 3000);
    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (dismissRef.current) clearTimeout(dismissRef.current);
    };
  }, [showNotification, scheduleNext]);
  const handleDismiss = () => {
    setNotification(null);
    if (dismissRef.current) clearTimeout(dismissRef.current);
  };
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none" data-id="element-162">
      <AnimatePresence mode="wait" data-id="element-163">
        {notification && <motion.div key={notification.id} initial={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} animate={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="pointer-events-auto bg-white rounded-xl shadow-[0_8px_30px_-4px_rgba(0,0,0,0.15)] border border-slate-100 px-5 py-4 flex items-center gap-4 max-w-sm w-[360px] cursor-pointer hover:shadow-[0_8px_40px_-4px_rgba(0,0,0,0.2)] transition-shadow" onClick={() => window.open('https://calendly.com/jesus-reinosobusinessconsultant/30min', '_blank')} data-id="element-164">
            <div className="flex-shrink-0 w-10 h-10 bg-brand-amber/10 rounded-full flex items-center justify-center" data-id="element-165">
              <CalendarCheck size={20} className="text-brand-amber" data-id="element-166" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-167">
              <p className="text-sm text-slate-800 leading-snug" data-id="element-168">
                <strong className="font-semibold" data-id="element-169">{notification.name}</strong>{' '}
                en <span className="text-slate-600" data-id="element-170">{notification.city}</span>{' '}
                {notification.message}
              </p>
              <p className="text-xs text-slate-400 mt-1" data-id="element-171">
                {notification.timeAgo}
              </p>
            </div>
            <button onClick={e => {
          e.stopPropagation();
          handleDismiss();
        }} className="flex-shrink-0 p-1 rounded-md hover:bg-slate-100 transition-colors text-slate-300 hover:text-slate-500" aria-label="Cerrar notificación" data-id="element-172">
              <X size={14} data-id="element-173" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}